import {
  SecurityTypesUtils,
  UpdateActionEnum,
  type CareOrder,
  type MinimalSubscriptionResponse,
  type Order,
  type Quote,
  type useSecuritiesContext,
} from '@talos/kyoko';
import { filter, map, merge, scan, shareReplay, startWith, type Observable } from 'rxjs';
import {
  createCareOrderBlotterEntity,
  LegRow,
  OrderRow,
  QuoteRow,
  type CareOrderBlotterEntity,
  type OrderWithParentOrderID,
  type QuoteWithParentRFQID,
} from '../types';

type RFQOrder = RequireKeys<Order, 'ParentOrderID' | 'RFQID'>;
type SecuritiesBySymbol = ReturnType<typeof useSecuritiesContext>['securitiesBySymbol'];
type CareOrderBlotterSource = 'careOrder' | 'workingOrder' | 'quote' | 'order';

export type CareOrderBlotterSourceMessage =
  | { source: 'careOrder'; message: MinimalSubscriptionResponse<CareOrder> }
  | { source: 'workingOrder'; message: MinimalSubscriptionResponse<CareOrder> }
  | { source: 'quote'; message: MinimalSubscriptionResponse<Quote> }
  | { source: 'order'; message: MinimalSubscriptionResponse<Order> };

/**
 * Whenever we handle a new incoming record for any of the streams, they are represented by one of these diffs.
 * The parentID is the branch root's CareOrder ID. Care Orders use their own .OrderID here.
 */
type EntityDiff<T> = { prev: T | undefined; next: T | undefined; parentID: string };

/**
 * Mutable memory for the Care Order blotter row graph.
 *
 * The websocket streams remain normalized here. Rendered rows are derived only at the branch boundary
 * so high-cadence Order updates can avoid rebuilding unrelated care-order branches.
 */
interface CareOrderBlotterPipelineState {
  initializedSources: Set<CareOrderBlotterSource>;
  hasEmittedInitial: boolean;

  // Raw websocket memory. These maps keep the latest version of each upstream entity in their totality.
  careOrdersByID: Map<string, CareOrder>;
  workingOrdersByID: Map<string, CareOrder>;
  quotesByRFQID: Map<string, Quote>;
  ordersByOrderID: Map<string, Order>;

  // Secondary indexes let a high-cadence Order update rebuild one parent branch instead of scanning all rows.
  quoteIDsByParentID: Map<string, Set<string>>;
  // Orders that do not have an RFQID on them - they are here on their own
  soloOrderIDsByParentID: Map<string, Set<string>>;
  // Orders that have a ParentRFQID on them - as in, they are a complement to some Quote entity in this pipe.
  rfqOrderIDsByRFQID: Map<string, Set<string>>;

  // Rendered row memory. This is what lets us emit explicit removals when a rebuilt branch has fewer rows.
  entitiesByRowID: Map<string, CareOrderBlotterEntity>;
  // State of every branch. Keyed by the ParentID (CareOrderID), and includes a set of all branch rowIDs. The main purpose of this
  // piece of memory is more easily tracking when to do removals. When we rebuild a branch as we process a new message,
  // the previous set of rowIDs within the same branch is compared to the new one. If any rowID is removed, we pass along a removal message
  // to the grid (UpdateAction: "Remove")
  branchRowIDsByParentID: Map<string, Set<string>>;
}

/**
 * The reducer emits a small payload for downstream observables.
 *
 * `output` is the delta/full message for the main AG Grid blotter, while `entitiesByRowID`
 * exposes the current rendered entity memory for branch-aware consumers.
 */
export interface CareOrderBlotterPipelineEmission {
  output: MinimalSubscriptionResponse<CareOrderBlotterEntity>;
  entitiesByRowID: Map<string, CareOrderBlotterEntity>;
}

interface CareOrderEntitiesMergerParams {
  enableCareOrderNetting: boolean;
  careOrdersObs: Observable<MinimalSubscriptionResponse<CareOrder>>;
  workingOrdersObs: Observable<MinimalSubscriptionResponse<CareOrder>>;
  quotesObs: Observable<MinimalSubscriptionResponse<Quote>>;
  ordersObs: Observable<MinimalSubscriptionResponse<Order>>;
  getSecuritiesBySymbol: () => SecuritiesBySymbol;
}

export function careOrderEntitiesMerger({
  enableCareOrderNetting,
  careOrdersObs,
  workingOrdersObs,
  quotesObs,
  ordersObs,
  getSecuritiesBySymbol,
}: CareOrderEntitiesMergerParams): Observable<CareOrderBlotterPipelineEmission> {
  const requiredSources: CareOrderBlotterSource[] = enableCareOrderNetting
    ? ['careOrder', 'workingOrder', 'quote', 'order']
    : ['careOrder', 'quote', 'order'];

  const sourceObservables: Observable<CareOrderBlotterSourceMessage>[] = [
    careOrdersObs.pipe(map(message => ({ source: 'careOrder', message }) as const)),
    quotesObs.pipe(map(message => ({ source: 'quote', message }) as const)),
    ordersObs.pipe(map(message => ({ source: 'order', message }) as const)),
  ];

  if (enableCareOrderNetting) {
    sourceObservables.push(
      workingOrdersObs.pipe(
        // Don't let the WorkingOrder stream hold up the rendering.
        startWith({ initial: true, data: [] }),
        map(message => ({ source: 'workingOrder', message }) as const)
      )
    );
  }

  return merge(...sourceObservables).pipe(
    scan(
      (acc, sourceMessage) => {
        const output = updateCareOrderBlotterPipelineState({
          state: acc.state,
          sourceMessage,
          requiredSources,
          securitiesBySymbol: getSecuritiesBySymbol(),
        });

        if (output == null) {
          // Nothing to do, just keep our state and wait for the next lap of the scan before we do anything
          return {
            state: acc.state,
            emission: undefined,
          };
        }

        return {
          state: acc.state,
          emission: {
            output,
            entitiesByRowID: acc.state.entitiesByRowID,
          },
        };
      },
      {
        state: createCareOrderBlotterPipelineState(),
        emission: undefined as CareOrderBlotterPipelineEmission | undefined,
      }
    ),
    filter((acc): acc is { emission: CareOrderBlotterPipelineEmission; state: CareOrderBlotterPipelineState } => {
      return acc.emission != null;
    }),
    map(acc => acc.emission),
    shareReplay({ bufferSize: 1, refCount: true })
  );
}

function isQuoteWithParentRFQID(quote: Quote): quote is QuoteWithParentRFQID {
  return quote.ParentRFQID != null;
}

function isDirectChildOrder(order: Order): order is OrderWithParentOrderID {
  return order.ParentOrderID != null && order.RFQID == null;
}

function isRFQOrder(order: Order): order is RFQOrder {
  return order.ParentOrderID != null && order.RFQID != null;
}

function createCareOrderBlotterPipelineState(): CareOrderBlotterPipelineState {
  return {
    initializedSources: new Set(),
    hasEmittedInitial: false,
    careOrdersByID: new Map(),
    workingOrdersByID: new Map(),
    quotesByRFQID: new Map(),
    ordersByOrderID: new Map(),

    quoteIDsByParentID: new Map(),
    soloOrderIDsByParentID: new Map(),
    rfqOrderIDsByRFQID: new Map(),

    entitiesByRowID: new Map(),
    branchRowIDsByParentID: new Map(),
  };
}

/**
 * Applies one websocket message to the raw-entity memory and returns the rows AG Grid should see.
 *
 * The first complete set of source initials produces a full snapshot. After that, normal updates
 * produce branch deltas: if any entity under a care-order branch changes, rebuild that branch only.
 */
function updateCareOrderBlotterPipelineState({
  state,
  sourceMessage,
  requiredSources,
  securitiesBySymbol,
}: {
  state: CareOrderBlotterPipelineState;
  sourceMessage: CareOrderBlotterSourceMessage;
  requiredSources: CareOrderBlotterSource[];
  securitiesBySymbol: SecuritiesBySymbol;
}) {
  const impactedParentIDs = applySourceMessage(state, sourceMessage);
  state.initializedSources.add(sourceMessage.source);

  // Wait until every required stream has provided its initial message before showing the first tree.
  if (!requiredSources.every(source => state.initializedSources.has(source))) {
    return undefined;
  }

  if (!state.hasEmittedInitial) {
    // Every source has now emitted its initial: true message, and our state is telling us that this is the first time all sources have done that.
    // We now build the initial absolute state of the blotter tree, and return that (initial: true)
    const data = rebuildAllRows(state, securitiesBySymbol);
    state.hasEmittedInitial = true;
    return { initial: true, data };
  }

  // Else down here, we have already done our initial: true emission to the blotter, and are just applying deltas.
  const data = Array.from(impactedParentIDs).flatMap(parentID => rebuildBranch(state, parentID, securitiesBySymbol));
  if (data.length > 0) {
    return { initial: false, data };
  }

  // Nothing to do.
  return undefined;
}

function applySourceMessage(
  state: CareOrderBlotterPipelineState,
  sourceMessage: CareOrderBlotterSourceMessage
): Set<string> {
  switch (sourceMessage.source) {
    case 'careOrder':
      return applyParentOrderMessage(state.careOrdersByID, sourceMessage.message);
    case 'workingOrder':
      return applyParentOrderMessage(state.workingOrdersByID, sourceMessage.message);
    case 'quote':
      return applyQuoteMessage(state, sourceMessage.message);
    case 'order':
      return applyOrderMessage(state, sourceMessage.message);
  }
}

// Recall that "parent orders" are care orders / working orders. Their treatment is simple.
function applyParentOrderMessage(map: Map<string, CareOrder>, message: MinimalSubscriptionResponse<CareOrder>) {
  return applyMessageToMap({
    map,
    message,
    getKey: careOrder => careOrder.OrderID,
    getParentID: careOrder => careOrder.OrderID,
  }).impactedParentIDs;
}

function applyQuoteMessage(state: CareOrderBlotterPipelineState, message: MinimalSubscriptionResponse<Quote>) {
  // We start off by applying to the main memory map - quotes by id
  const { diff, impactedParentIDs } = applyMessageToMap({
    map: state.quotesByRFQID,
    message,
    getKey: quote => quote.RFQID,
    getParentID: quote => quote.ParentRFQID,
  });

  // Below here we apply the same messages to the indexes (we only have one here - quoteIDsByParentID)
  if (message.initial) {
    state.quoteIDsByParentID.clear();
  }
  for (const { prev, next } of diff.values()) {
    if (prev != null) {
      removeFromKeyMapping(state.quoteIDsByParentID, prev.ParentRFQID, prev.RFQID);
    }
    if (next != null) {
      addToKeyMapping(state.quoteIDsByParentID, next.ParentRFQID, next.RFQID);
    }
  }

  return impactedParentIDs;
}

function applyOrderMessage(state: CareOrderBlotterPipelineState, message: MinimalSubscriptionResponse<Order>) {
  // Start off by applying the message to the main memory map - orders by id
  const { diff, impactedParentIDs } = applyMessageToMap({
    map: state.ordersByOrderID,
    message,
    getKey: order => order.OrderID,
    getParentID: order => order.ParentOrderID,
  });

  // We then apply the order updates to our indexes, of which we have two
  if (message.initial) {
    state.soloOrderIDsByParentID.clear();
    state.rfqOrderIDsByRFQID.clear();
  }

  for (const { prev, next, parentID } of diff.values()) {
    // Remove whatever might exist based on prev. Recall that we put orders into one of two maps depending on if they are coupled to an RFQ or not
    if (prev != null) {
      if (prev.RFQID == null) {
        removeFromKeyMapping(state.soloOrderIDsByParentID, parentID, prev.OrderID);
      } else {
        removeFromKeyMapping(state.rfqOrderIDsByRFQID, prev.RFQID, prev.OrderID);
      }
    }

    // And add whatever we can for next
    if (next != null) {
      if (next.RFQID == null) {
        addToKeyMapping(state.soloOrderIDsByParentID, parentID, next.OrderID);
      } else {
        addToKeyMapping(state.rfqOrderIDsByRFQID, next.RFQID, next.OrderID);
      }
    }
  }

  return impactedParentIDs;
}

/**
 * Does basic message application to a map, including UpdateAction.Remove handling.
 * Returns the applied diff per key. Each diff entry is { prev, next, parentID }. Also returns a unique set of
 * these parentIDs for easier consumption.
 *
 * Later `initial: true` messages (e.g. reconnect) replace the whole map. Any previously remembered entity
 * missing from that snapshot is treated as a Remove so branch rebuilds can emit AG Grid removals.
 */
function applyMessageToMap<T>({
  map,
  message,
  getKey,
  getParentID,
}: {
  map: Map<string, T>;
  message: MinimalSubscriptionResponse<T>;
  getKey: (item: T) => string;
  getParentID: (item: T) => string | undefined;
}) {
  const diff = new Map<string, EntityDiff<T>>();
  if (message.initial) {
    appendInitialRemovalsToDiff({ map, message, getKey, getParentID, diff });
    map.clear();
  }

  for (const update of message.data) {
    const key = getKey(update);
    const parentID = getParentID(update);
    if (!parentID) {
      // If the entity doesn't have a parentID, its not relevant to us
      // (CareOrders return their own ID)
      continue;
    }
    const prev = map.get(key);
    const isRemove = getUpdateAction(update, message) === UpdateActionEnum.Remove;
    const next = isRemove ? undefined : update;
    diff.set(key, { prev, next, parentID });
    if (next == null) {
      map.delete(key);
    } else {
      map.set(key, next);
    }
  }

  // For easier consumption, expose this set of unique impacted parent ids ("branch roots") right away
  const impactedParentIDs = new Set([...diff.values()].map(({ parentID }) => parentID));
  return { diff, impactedParentIDs };
}

/**
 * When a stream re-sends `initial: true`, entities still in memory but absent from the new snapshot
 * must become Remove diffs so impacted branches emit UpdateAction.Remove rows downstream.
 */
function appendInitialRemovalsToDiff<T>({
  map,
  message,
  getKey,
  getParentID,
  diff,
}: {
  map: Map<string, T>;
  message: MinimalSubscriptionResponse<T>;
  getKey: (item: T) => string;
  getParentID: (item: T) => string | undefined;
  diff: Map<string, EntityDiff<T>>;
}) {
  const nextKeys = new Set<string>();
  for (const update of message.data) {
    if (getParentID(update) == null) {
      continue;
    }
    if (getUpdateAction(update, message) === UpdateActionEnum.Remove) {
      continue;
    }
    nextKeys.add(getKey(update));
  }

  for (const [key, prev] of map) {
    if (nextKeys.has(key)) {
      continue;
    }
    const parentID = getParentID(prev);
    if (parentID == null) {
      continue;
    }
    diff.set(key, { prev, next: undefined, parentID });
  }
}

function getUpdateAction<T>(update: T, message: MinimalSubscriptionResponse<T>) {
  return (update as { UpdateAction?: UpdateActionEnum }).UpdateAction ?? message.action;
}

function addToKeyMapping(map: Map<string, Set<string>>, key: string | undefined, value: string) {
  if (key == null) {
    return;
  }
  const values = map.get(key) ?? new Set<string>();
  values.add(value);
  map.set(key, values);
}

function removeFromKeyMapping(map: Map<string, Set<string>>, key: string | undefined, value: string) {
  if (key == null) {
    return;
  }
  const values = map.get(key);
  values?.delete(value);
  if (values?.size === 0) {
    map.delete(key);
  }
}

/** Rebuilds the complete rendered tree after the first load or a source re-initialization */
function rebuildAllRows(state: CareOrderBlotterPipelineState, securitiesBySymbol: SecuritiesBySymbol) {
  // We're rebuilding, so clear the current state of existing row entities
  state.entitiesByRowID.clear();
  state.branchRowIDsByParentID.clear();

  const rows: CareOrderBlotterEntity[] = [];
  const parentIDs = new Set<string>();

  // Pull together all the parent ids. These are the roots for all the branches.
  for (const careOrder of state.careOrdersByID.values()) {
    if (careOrder.WorkingOrderID == null) {
      parentIDs.add(careOrder.OrderID);
    }
  }
  for (const workingOrder of state.workingOrdersByID.values()) {
    parentIDs.add(workingOrder.OrderID);
  }

  // Then, for every parent id (branch root), build that branch
  for (const parentID of parentIDs) {
    for (const row of rebuildBranch(state, parentID, securitiesBySymbol)) {
      // And once the branch is built, push each row of the branch out to the blotter
      rows.push(row);
    }
  }

  return rows;
}

function rebuildBranch(state: CareOrderBlotterPipelineState, parentID: string, securitiesBySymbol: SecuritiesBySymbol) {
  const previousRowIDs = state.branchRowIDsByParentID.get(parentID) ?? new Set<string>();
  const nextRows = buildBranchRows(state, parentID, securitiesBySymbol);
  const nextRowIDs = new Set(nextRows.map(row => row.rowID));

  const outputRows: CareOrderBlotterEntity[] = [];

  // Check to see if any row id previously pushed to the blotter no longer exists in the new revision of this branch.
  // If so, push a removal message to the blotter so its AgGrid removes it (UpdateAction.Remove)
  for (const previousRowID of previousRowIDs) {
    if (!nextRowIDs.has(previousRowID)) {
      const previousEntity = state.entitiesByRowID.get(previousRowID);
      state.entitiesByRowID.delete(previousRowID);
      if (previousEntity != null) {
        outputRows.push(createRemoveRow(previousEntity));
      }
    }
  }

  for (const row of nextRows) {
    state.entitiesByRowID.set(row.rowID, row); // update our memory
    outputRows.push(row); // push our new row to be sent to the blotter
  }

  if (nextRowIDs.size === 0) {
    state.branchRowIDsByParentID.delete(parentID); // nothing in the branch, so delete it from memory
  } else {
    state.branchRowIDsByParentID.set(parentID, nextRowIDs);
  }

  return outputRows;
}

/**
 * Builds one visible tree branch from normalized memory.
 *
 * Returning an empty array means the parent is currently not visible, either because it is missing
 * or because a care order has been netted into a working order.
 */
function buildBranchRows(
  state: CareOrderBlotterPipelineState,
  parentID: string,
  securitiesBySymbol: SecuritiesBySymbol
) {
  // Grab either the care order or working order based on some logic. If we cant find anything, no-op.
  const parentOrder = getParentOrder(state, parentID);
  if (parentOrder == null) {
    return [];
  }

  const quoteRows = Array.from(state.quoteIDsByParentID.get(parentID) ?? []).flatMap(rfqID => {
    const quote = state.quotesByRFQID.get(rfqID);
    return quote != null && isQuoteWithParentRFQID(quote) ? [enrichQuoteRow(state, new QuoteRow(quote))] : [];
  });

  const orderRows = Array.from(state.soloOrderIDsByParentID.get(parentID) ?? []).flatMap(orderID => {
    const order = state.ordersByOrderID.get(orderID);
    return order != null && isDirectChildOrder(order) ? [new OrderRow(order)] : [];
  });

  const parentRow = createCareOrderBlotterEntity(parentOrder, [...quoteRows, ...orderRows]);
  const rows: CareOrderBlotterEntity[] = [parentRow];

  for (const quoteRow of quoteRows) {
    rows.push(quoteRow);

    // Derive any leg rows based on quotes here.
    for (const legRow of getLegRows(quoteRow, securitiesBySymbol)) {
      rows.push(legRow);
    }
  }

  for (const orderRow of orderRows) {
    rows.push(orderRow);
  }

  return rows;
}

// Given a parentID (CareOrder/WorkingOrder ID), get either a CareOrder or WorkingOrder. There's some logic, so it gets its own fn.
function getParentOrder(state: CareOrderBlotterPipelineState, parentID: string) {
  const workingOrder = state.workingOrdersByID.get(parentID);
  if (workingOrder != null) {
    // Working orders render with CareOrderRow semantics and take precedence over same-ID care orders.
    return workingOrder;
  }

  const careOrder = state.careOrdersByID.get(parentID);
  // Netted care orders remain in source memory, but they should disappear from the tree.
  return careOrder?.WorkingOrderID == null ? careOrder : undefined;
}

function enrichQuoteRow(state: CareOrderBlotterPipelineState, quoteRow: QuoteRow) {
  const rfqOrder = getRFQOrderForQuote(state, quoteRow.RFQID);
  return rfqOrder == null ? quoteRow : quoteRow.enrichWith(rfqOrder);
}

function getRFQOrderForQuote(state: CareOrderBlotterPipelineState, rfqID: string) {
  let rfqOrder: RFQOrder | undefined;
  for (const orderID of state.rfqOrderIDsByRFQID.get(rfqID) ?? []) {
    const order = state.ordersByOrderID.get(orderID);
    if (order != null && isRFQOrder(order)) {
      // Preserve old full-rebuild behavior: if multiple RFQ Orders match, the last map-iteration match wins.
      rfqOrder = order;
    }
  }
  return rfqOrder;
}

// Compound security quotes get their legs shown as child rows to the quote, which is pretty cool
function getLegRows(quoteRow: QuoteRow, securitiesBySymbol: SecuritiesBySymbol) {
  const security = securitiesBySymbol.get(quoteRow.Symbol);
  const isCompoundSecurity = SecurityTypesUtils.Compound.isType(security);
  if (!isCompoundSecurity || quoteRow.legsRepresentative.length === 0) {
    return [];
  }

  return quoteRow.legsRepresentative.map(representativeLeg => new LegRow({ quote: quoteRow, representativeLeg }));
}

function createRemoveRow(entity: CareOrderBlotterEntity): CareOrderBlotterEntity {
  entity.UpdateAction = UpdateActionEnum.Remove;
  return entity;
}
